import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { createApiUrlWithPrefix, createApiUrl } from '../config/api';
import { Check, X, Eye, Trash2, RefreshCw, FileText, Camera, Search } from 'lucide-react';
import UserCreationModal from './UserCreationModal';
import Modal, { ModalFooterBar } from './ui/Modal';
import { useUserCounts } from '../contexts/UserCountsContext';

function Subscribers() {
  const { t } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('0'); // Default to '0' = Pending only
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== '') {
        params.append('status', statusFilter);
      }
      params.append('page', page);
      params.append('pageSize', pageSize);

      const queryString = params.toString();
      const url = createApiUrlWithPrefix(`subscribers${queryString ? `?${queryString}` : ''}`);
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.items) {
        setSubscribers(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
        // Update pending count based on API totalItems (which reflects filtered pending if status=0)
        setPendingCount(response.data.totalItems || 0);
      } else {
        // Fallback for backward compatibility or error
        const list = Array.isArray(response.data) ? response.data : [];
        setSubscribers(list);
        // Compute pending count manually when not provided
        const pending = list.filter(s => s.status === 0).length;
        setPendingCount(pending);
      }
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
      toast.error(error.response?.data?.message || t('subscribers.toastLoadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize, t]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleApprove = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setShowApproveModal(true);
  };

  const handleUserCreated = async (newUser) => {
    // After user is created via the form, mark the subscriber as converted
    if (selectedSubscriber && newUser && newUser.id) {
      try {
        // Mark subscriber as converted with the newly created user ID
        await axios.put(
          createApiUrlWithPrefix(`subscribers/${selectedSubscriber.id}/convert`),
          newUser.id, // Send userId as body
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        toast.success(t('subscribers.toastApproveSuccess'));
        setShowApproveModal(false);
        setSelectedSubscriber(null);

        // Force refresh the subscribers list to remove the approved subscriber
        // Ensure we're still filtering by pending status (0)
        setStatusFilter('0');
        await fetchSubscribers();
        refreshUserCounts(); // Refresh the subscriber count in navigation
      } catch (error) {
        console.error('Failed to mark subscriber as converted:', error);
        // Even if marking as converted fails, the user was created successfully
        toast.success(t('subscribers.toastConvertSuccess'));
        setShowApproveModal(false);
        setSelectedSubscriber(null);

        // Force refresh even on error
        setStatusFilter('0');
        await fetchSubscribers();
        refreshUserCounts();
      }
    }
  };

  const handleReject = async (subscriberId) => {
    if (!window.confirm(t('subscribers.confirmReject'))) {
      return;
    }

    try {
      await axios.post(
        createApiUrlWithPrefix(`subscribers/${subscriberId}/reject`),
        null,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success(t('subscribers.toastRejectSuccess'));
      // Ensure filter is set to pending and refresh
      setStatusFilter('0');
      await fetchSubscribers();
      refreshUserCounts(); // Refresh the subscriber count in navigation
    } catch (error) {
      console.error('Failed to reject subscriber:', error);
      toast.error(error.response?.data?.message || t('subscribers.toastRejectError'));
    }
  };

  const handleDelete = async (subscriberId) => {
    if (!window.confirm(t('subscribers.confirmDelete'))) {
      return;
    }

    try {
      await axios.delete(createApiUrlWithPrefix(`subscribers/${subscriberId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success(t('subscribers.toastDeleteSuccess'));
      // Ensure filter is set to pending and refresh
      setStatusFilter('0');
      await fetchSubscribers();
      refreshUserCounts(); // Refresh the subscriber count in navigation
    } catch (error) {
      console.error('Failed to delete subscriber:', error);
      toast.error(error.response?.data?.message || t('subscribers.toastDeleteError'));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: t('subscribers.statusPending'), color: 'bg-yellow-100 text-yellow-800' },
      1: { label: t('subscribers.statusApproved'), color: 'bg-green-100 text-green-800' },
      2: { label: t('subscribers.statusRejected'), color: 'bg-red-100 text-red-800' },
      3: { label: t('subscribers.statusConverted'), color: 'bg-blue-100 text-blue-800' }
    };

    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('subscribers.notAvailable');
    return new Date(dateString).toLocaleDateString();
  };

  const memoizedInitialData = React.useMemo(() => {
    if (!selectedSubscriber) return null;
    return {
      id: selectedSubscriber.id,
      firstName: selectedSubscriber.firstName,
      lastName: selectedSubscriber.lastName,
      email: selectedSubscriber.email,
      phoneNumber: selectedSubscriber.phoneNumber,
      birthDate: selectedSubscriber.birthDate ? selectedSubscriber.birthDate.split('T')[0] : '',
      address: selectedSubscriber.address,
      bloodType: selectedSubscriber.bloodType,
      photoPath: selectedSubscriber.photoPath,
      pjDocumentPath: selectedSubscriber.pjDocumentPath,
      birthCertificatePath: selectedSubscriber.birthCertificatePath,
    };
  }, [selectedSubscriber]);

  const [searchTerm, setSearchTerm] = useState('');

  // Client-side search filter
  const filteredSubscribers = subscribers.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const fullName = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
    return (
      fullName.includes(term) ||
      (s.email && s.email.toLowerCase().includes(term)) ||
      (s.phoneNumber && s.phoneNumber.toLowerCase().includes(term))
    );
  });

  return (
    <div className="flex flex-col min-h-full bg-neutral-50 pb-8">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">{t('subscribers.title')}</h1>
              {pendingCount > 0 && (
                <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full animate-pulse">
                  {pendingCount} {t('subscribers.pending')}
                </span>
              )}
            </div>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-neutral-600">
              {t('subscribers.subtitle', 'Demandes d\'inscription et pré-inscriptions en ligne')}
            </p>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 mt-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute top-1/2 transform -translate-y-1/2 left-4 h-5 w-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder={t('subscribers.searchPlaceholder', 'Rechercher un souscripteur (Nom, email, tél)...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-4 text-base border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
              </div>
            </div>

            {/* Filter Dropdown + Actions */}
            <div className="flex items-center gap-3 w-full lg:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="py-3 px-4 text-sm font-medium border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white transition-all"
              >
                <option value="0">⏳ {t('subscribers.statusPending')}</option>
                <option value="">📋 {t('subscribers.statusFilterAll')}</option>
                <option value="1">✅ {t('subscribers.statusApproved')}</option>
                <option value="2">❌ {t('subscribers.statusRejected')}</option>
                <option value="3">🔄 {t('subscribers.statusConverted')}</option>
              </select>

              <button
                onClick={() => setSearchTerm('')}
                className="px-5 py-3 text-sm font-medium text-neutral-600 border border-neutral-300 rounded-xl hover:bg-neutral-50 transition-colors"
              >
                {t('common.clear', 'Effacer')}
              </button>

              <button
                onClick={fetchSubscribers}
                className="p-3 text-neutral-600 border border-neutral-300 rounded-xl hover:bg-neutral-50 transition-colors"
                title={t('subscribers.refresh')}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-neutral-400 bg-white rounded-xl border border-neutral-200">
            <span className="text-5xl mb-3">🧑‍🎓</span>
            <p className="text-lg font-medium">{t('subscribers.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {filteredSubscribers.map((subscriber) => {
              const fullName = `${subscriber.firstName || ''} ${subscriber.lastName || ''}`.trim();
              const hasPhoto = !!(subscriber.photoPath || subscriber.PhotoPath);
              const hasPJ    = !!(subscriber.pjDocumentPath || subscriber.PJDocumentPath);
              const hasBirth = !!(subscriber.birthCertificatePath || subscriber.BirthCertificatePath);

              return (
                <div
                  key={subscriber.id}
                  className="relative bg-white rounded-xl shadow-md border border-neutral-200 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  {/* Card Top Banner / Status Badge */}
                  <div className="flex items-center justify-between p-3.5 pb-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700">
                      🧑‍🎓 Souscripteur
                    </span>
                    {getStatusBadge(subscriber.status)}
                  </div>

                  {/* Info Body */}
                  <div className="px-4 pb-4">
                    {/* Avatar */}
                    <div className="flex justify-center mb-3">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center border-2 border-indigo-200 shadow-sm text-indigo-700 font-bold text-xl">
                        {fullName.charAt(0)?.toUpperCase() || 'S'}
                      </div>
                    </div>

                    {/* Name & Email */}
                    <div className="text-center mb-3">
                      <h3 className="text-lg font-semibold text-neutral-900 truncate">
                        {fullName || t('subscribers.unknown', 'Anonyme')}
                      </h3>
                      <p className="text-sm text-neutral-500 truncate">{subscriber.email}</p>
                    </div>

                    {/* Meta info chips */}
                    <div className="space-y-1.5 text-xs text-neutral-600 mb-3">
                      {subscriber.phoneNumber && (
                        <div className="flex items-center justify-center">
                          <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                            📞 {subscriber.phoneNumber}
                          </span>
                        </div>
                      )}

                      {subscriber.createdAt && (
                        <div className="flex items-center justify-center">
                          <span className="bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full font-medium">
                            📅 {formatDate(subscriber.createdAt)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Documents Indicator Strip */}
                    <div className="rounded-xl border border-neutral-100 bg-neutral-50/80 px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
                        Documents
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${hasPhoto ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          📸 Photo {hasPhoto ? '✓' : '✗'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${hasPJ ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          🪪 PJ {hasPJ ? '✓' : '✗'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${hasBirth ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          📄 Naissance {hasBirth ? '✓' : '✗'}
                        </span>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <button
                      onClick={() => {
                        setSelectedSubscriber(subscriber);
                        setShowDetailsModal(true);
                      }}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 rounded-xl text-sm font-semibold hover:bg-indigo-100 border border-indigo-200 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      {t('subscribers.viewDetails')}
                    </button>

                    {/* Direct Action Buttons (no dropdown) */}
                    {subscriber.status === 0 && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleApprove(subscriber)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          {t('subscribers.approve')} & Convert
                        </button>

                        <button
                          onClick={() => handleReject(subscriber.id)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-700 rounded-xl text-sm font-semibold hover:bg-red-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          {t('subscribers.reject')}
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => handleDelete(subscriber.id)}
                      className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100 border border-red-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('subscribers.delete')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Standard Pagination Footer */}
        {totalItems > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-neutral-600">
              {t('subscribers.showing')} <span className="font-bold text-primary-600">{(page - 1) * pageSize + 1}</span> {t('subscribers.to')} <span className="font-bold text-primary-600">{Math.min(page * pageSize, totalItems)}</span> {t('subscribers.of')} <span className="font-bold text-primary-600">{totalItems}</span> {t('subscribers.results')}
            </p>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-neutral-300 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 transition-all"
              >
                {t('subscribers.previous')}
              </button>

              <span className="text-sm font-bold text-neutral-700 px-3">
                {page} / {totalPages || 1}
              </span>

              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-neutral-300 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 transition-all"
              >
                {t('subscribers.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal && !!selectedSubscriber}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSubscriber(null);
        }}
        title={t('subscribers.detailsModalTitle')}
        subtitle={selectedSubscriber ? `${selectedSubscriber.firstName} ${selectedSubscriber.lastName}` : ''}
        icon={Eye}
        variant="indigo"
        size="lg"
        footer={
          <ModalFooterBar>
            <button
              type="button"
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedSubscriber(null);
              }}
              className="px-5 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              {t('subscribers.close')}
            </button>
          </ModalFooterBar>
        }
      >
        {selectedSubscriber && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelFirstName')}</label>
                <p className="text-gray-900">{selectedSubscriber.firstName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelLastName')}</label>
                <p className="text-gray-900">{selectedSubscriber.lastName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelEmail')}</label>
                <p className="text-gray-900">{selectedSubscriber.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelPhoneNumber')}</label>
                <p className="text-gray-900">{selectedSubscriber.phoneNumber || t('subscribers.notAvailable')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelBirthDate')}</label>
                <p className="text-gray-900">{formatDate(selectedSubscriber.birthDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelBloodType')}</label>
                <p className="text-gray-900">{selectedSubscriber.bloodType || t('subscribers.notAvailable')}</p>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelAddress')}</label>
                <p className="text-gray-900">{selectedSubscriber.address || t('subscribers.notAvailable')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelStatus')}</label>
                <div className="mt-1">{getStatusBadge(selectedSubscriber.status)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t('subscribers.labelCreatedAt')}</label>
                <p className="text-gray-900">{formatDate(selectedSubscriber.createdAt)}</p>
              </div>
              {selectedSubscriber.notes && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">{t('subscribers.labelNotes')}</label>
                  <p className="text-gray-900">{selectedSubscriber.notes}</p>
                </div>
              )}

              {/* Uploaded Documents */}
              <div className="col-span-2 mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-3">{t('subscribers.uploadedDocuments')}</label>
                <div className="flex gap-8">
                  {/* Photo */}
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('subscribers.profilePhoto')}</p>
                    {selectedSubscriber.photoPath ? (
                      <a href={createApiUrl(selectedSubscriber.photoPath)} target="_blank" rel="noopener noreferrer" className="block relative group">
                        <img
                          src={createApiUrl(selectedSubscriber.photoPath)}
                          alt={t('subscribers.profilePhoto')}
                          className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200 shadow-sm group-hover:border-primary-500 transition-colors"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all">
                          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                        </div>
                      </a>
                    ) : (
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                        <Camera className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs">{t('subscribers.noPhoto')}</span>
                      </div>
                    )}
                  </div>

                  {/* PJ Document */}
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('subscribers.inscriptionDoc')}</p>
                    {selectedSubscriber.pjDocumentPath ? (
                      <a
                        href={createApiUrl(selectedSubscriber.pjDocumentPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-32 h-32 bg-blue-50 rounded-xl flex flex-col items-center justify-center border-2 border-blue-100 text-blue-600 hover:bg-blue-100 transition-colors group"
                      >
                        <FileText className="w-10 h-10 mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs font-medium">{t('subscribers.viewDocument')}</span>
                      </a>
                    ) : (
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                        <FileText className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs">{t('subscribers.noDocument')}</span>
                      </div>
                    )}
                  </div>

                  {/* Birth Certificate */}
                  <div className="text-center">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('subscribers.birthCertificate')}</p>
                    {selectedSubscriber.birthCertificatePath ? (
                      <a
                        href={createApiUrl(selectedSubscriber.birthCertificatePath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-32 h-32 bg-green-50 rounded-xl flex flex-col items-center justify-center border-2 border-green-100 text-green-600 hover:bg-green-100 transition-colors group"
                      >
                        <FileText className="w-10 h-10 mb-2 group-hover:scale-110 transition-transform" />
                        <span className="text-xs font-medium">{t('subscribers.viewDocument')}</span>
                      </a>
                    ) : (
                      <div className="w-32 h-32 bg-gray-50 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 text-gray-400">
                        <FileText className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs">{t('subscribers.noDocument')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Approve Modal - Use same form as Riders page */}
      {showApproveModal && selectedSubscriber && (
        <UserCreationModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setSelectedSubscriber(null);
          }}
          role={null}
          mode="approve"
          onSubmit={async (approveData) => {
            // Use the approve endpoint
            try {
              const response = await axios.post(
                createApiUrlWithPrefix(`subscribers/${selectedSubscriber.id}/approve`),
                approveData,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );

              const newUser = response.data.User || response.data.user;

              toast.success(t('subscribers.toastApproveSuccess'));
              setShowApproveModal(false);
              setSelectedSubscriber(null);
              setStatusFilter('0');
              await fetchSubscribers();
              refreshUserCounts();
            } catch (error) {
              console.error('Failed to approve subscriber:', error);
              toast.error(error.response?.data?.message || t('subscribers.toastApproveError'));
              throw error;
            }
          }}
          onUserCreated={handleUserCreated}
          initialData={memoizedInitialData}
        />
      )}
    </div>
  );
}

export default Subscribers;
